"use client";

import { useState } from "react";
import Link from "next/link";

interface BlogSubscriptionBoxProps {
  title?: string;
  description?: string;
  buttonText?: string;
  theme?: "dark" | "light";
  showIllustration?: boolean;
}

export default function BlogSubscriptionBox({
  title = "Ready to Transform Your First Song?",
  description = "Join thousands of producers & clients who use ChangeLyric.",
  buttonText = "Start Free Trial",
  theme = "dark",
  showIllustration = true,
}: BlogSubscriptionBoxProps) {
  const [skillsAcknowledged, setSkillsAcknowledged] = useState(false);

  const bgColor = theme === "dark" ? "bg-base-300" : "bg-base-200";
  const textColor = theme === "dark" ? "text-base-content" : "text-base-content";
  const descriptionColor = theme === "dark" ? "text-base-content/70" : "text-base-content/70";
  const borderColor = theme === "dark" ? "border-base-300" : "border-base-200";

  return (
    <div className={`${bgColor} rounded-xl shadow-lg p-6 my-12 border ${borderColor}`}>
      <div className="flex flex-col md:flex-row gap-6 items-center">
        {showIllustration && (
          <div className="md:w-1/4 flex justify-center">
            <div className="relative">
              <svg
                className="w-24 h-24 text-primary animate-pulse"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-24 h-24">
                  <span className="absolute top-2 right-4 text-primary text-xl animate-bounce" style={{ animationDelay: '0s' }}>♪</span>
                  <span className="absolute top-8 left-2 text-primary/60 text-lg animate-bounce" style={{ animationDelay: '0.5s' }}>♫</span>
                  <span className="absolute bottom-8 right-2 text-primary/40 text-base animate-bounce" style={{ animationDelay: '1s' }}>♪</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`${showIllustration ? 'md:w-3/4' : 'w-full'} text-center md:text-left`}>
          <h3 className={`text-xl md:text-2xl font-bold mb-3 ${textColor}`}>
            {title}
          </h3>

          <p className={`mb-6 ${descriptionColor}`}>
            {description}
          </p>

          {!skillsAcknowledged ? (
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center md:justify-start">
              <button
                type="button"
                onClick={() => setSkillsAcknowledged(true)}
                className="btn btn-primary btn-md shrink h-auto min-h-12 whitespace-normal"
              >
                I have audio editing skills, continue
              </button>
              <Link
                href="/service"
                className="btn btn-outline btn-md shrink h-auto min-h-12 whitespace-normal"
              >
                Not me, hire your team
              </Link>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center md:justify-start">
              <Link
                href="/signin"
                className="btn btn-primary btn-md shrink h-auto min-h-12 whitespace-normal"
              >
                {buttonText}
              </Link>
              <button
                type="button"
                onClick={() => setSkillsAcknowledged(false)}
                className="btn btn-ghost btn-md shrink h-auto min-h-12 whitespace-normal text-base-content/60"
              >
                Wait, I&apos;m not comfortable with audio editing
              </button>
            </div>
          )}

          <p className={`mt-4 text-sm ${descriptionColor}`}>
            ✓ Free trial available &nbsp;&nbsp; ✓ No content moderation &nbsp;&nbsp; ✓ Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}
